import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order") || "";
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, localStatus: null, wallidStatus: null });

  useSEO({
    title: "Payment received",
    description: "Sterling Bio Pay-by-Bank payment confirmation.",
    path: "/payment/success",
  });

  useEffect(() => {
    if (!orderId || !token) {
      setState({ loading: false, localStatus: "unknown", wallidStatus: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/payments/wallid/status", { params: { order_id: orderId, token } });
        if (cancelled) return;
        setState({ loading: false, localStatus: r.data.local_status, wallidStatus: r.data.wallid_status });
      } catch {
        if (cancelled) return;
        setState({ loading: false, localStatus: "error", wallidStatus: null });
      }
    };
    poll();
    // Some banks take a few seconds after redirect before the webhook lands.
    const interval = setInterval(poll, 4000);
    // Stop polling after 60s.
    const stop = setTimeout(() => clearInterval(interval), 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [orderId, token]);

  const paid = state.localStatus === "completed" || state.wallidStatus === "SUCCESS";

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
      {state.loading ? (
        <div className="text-[#64748B] flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Confirming payment…
        </div>
      ) : paid ? (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#047857] mx-auto" />
          <h1
            className="mt-6 text-4xl font-semibold text-[#1B3A5C] tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Payment received
          </h1>
          <p className="mt-4 text-[#475569]">
            Thanks for your order. A confirmation email is on its way. Your parcel will be
            dispatched same-day (UK) if the order was placed before 14:00 GMT.
          </p>
        </>
      ) : (
        <>
          <Clock className="w-14 h-14 text-[#2C8C9C] mx-auto" />
          <h1
            className="mt-6 text-4xl font-semibold text-[#1B3A5C] tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Awaiting bank confirmation
          </h1>
          <p className="mt-4 text-[#475569]">
            Your bank is confirming the transfer. This usually takes a few seconds but can take up
            to 2 hours. You&apos;ll receive an email as soon as payment settles — no need to keep
            this page open.
          </p>
        </>
      )}
      <div className="mt-10 flex items-center justify-center gap-6">
        <Link to="/products" className="text-sm text-[#2C8C9C] sb-link-underline">
          ← Continue browsing
        </Link>
        {orderId ? (
          <Link
            to={`/order/${orderId}?token=${encodeURIComponent(token)}`}
            className="text-sm text-[#2C8C9C] sb-link-underline"
          >
            View order details →
          </Link>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentSuccess;
